"use client"

import { useEffect, useState } from "react"
import { useTranslations } from "next-intl"
import { useAuthStore } from "@/lib/auth-store"
import { fetchSeenState, markSeen } from "@/lib/client/seen-state"
import { useModalSlot } from "@/lib/client/modal-coordinator"
import { useSettingsStore } from "@/lib/settings-store"
import { useTagSetStore } from "@/lib/tag-set-store"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "@/lib/icons"

// One-time hint for the next-tag swipe (components/tag-step-gesture.tsx). It appears only once the user has TWO different tag
// searches (two sets that each hold a tag, not two tags in one search) and results are on screen, so it never lands on top of
// the first-visit popups. It dims the screen, says how to swipe, and goes away on tap, on "Got it", or as soon as the user
// really swipes. Seen once per account (guests: per browser). Re-read any time in Settings > Swipe between tags.
const SEEN_KEY = "modal:swipe_tutorial"
const LOCAL_SEEN_KEY = "goonru_swipe_tutorial_seen"
const SHOW_DELAY_MS = 1200

export function SwipeTutorial({ hasResults }: { hasResults: boolean }) {
  const t = useTranslations("swipeTutorial")
  const { user, profile, isLoading } = useAuthStore()
  const gestureOn = useSettingsStore((state) => state.tagStepGesture)
  const searchCount = useTagSetStore((state) => state.sets.filter((set) => set.tags.length > 0).length)
  const stepping = useTagSetStore((state) => state.focus !== null)

  const [unseen, setUnseen] = useState(false)
  const [ready, setReady] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  // Has this person seen it already?
  useEffect(() => {
    if (isLoading) return
    let cancelled = false
    async function check() {
      if (user && profile) {
        const { keys } = await fetchSeenState()
        if (!cancelled) setUnseen(!keys.has(SEEN_KEY))
      } else if (!user) {
        try {
          if (!cancelled) setUnseen(!localStorage.getItem(LOCAL_SEEN_KEY))
        } catch {
          if (!cancelled) setUnseen(true)
        }
      }
    }
    void check()
    return () => {
      cancelled = true
    }
  }, [user, profile, isLoading])

  const eligible = unseen && !dismissed && gestureOn && searchCount >= 2 && hasResults

  // Give the results a moment to appear before dimming the screen.
  useEffect(() => {
    if (!eligible) {
      setReady(false)
      return
    }
    const timer = setTimeout(() => setReady(true), SHOW_DELAY_MS)
    return () => clearTimeout(timer)
  }, [eligible])

  const canShow = useModalSlot("swipeTutorial", ready && eligible)

  function dismiss() {
    if (user) {
      void markSeen(SEEN_KEY)
    } else {
      try {
        localStorage.setItem(LOCAL_SEEN_KEY, "true")
      } catch {}
    }
    setDismissed(true)
  }

  // Swiping for real is the lesson learned: close it.
  useEffect(() => {
    if (canShow && stepping) dismiss()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepping])

  if (!canShow) return null
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 px-6 backdrop-blur-[2px]"
      onClick={dismiss}
      data-testid="swipe-tutorial"
      role="status"
    >
      <div className="flex max-w-xs flex-col items-center gap-4 text-center text-white">
        <div className="flex items-center gap-3" aria-hidden="true">
          <ChevronLeft className="h-8 w-8 motion-safe:animate-pulse" />
          <div className="h-1.5 w-24 rounded-full bg-white/70" />
          <ChevronRight className="h-8 w-8 motion-safe:animate-pulse" />
        </div>
        <p className="text-lg font-semibold">{t("title")}</p>
        <p className="text-sm text-white/80">{t("body")}</p>
        <Button
          variant="secondary"
          onClick={(e) => {
            e.stopPropagation()
            dismiss()
          }}
        >
          {t("gotIt")}
        </Button>
      </div>
    </div>
  )
}
